// TERRAIN HEIGHTS (TERRAIN 1 of 3): every square has a fixed terrain height 1..4, sent by the
// server as game.terrain {"r,c": height}. The sea itself is level 0.
// 3D BOARD: every square is drawn as a raised block seen slightly from above and to the right:
// its top face (S x S) is shifted up-left by LEVEL_PX per height level, two side faces (right and
// bottom) fill the space it moved away from. Squares are drawn row by row from the top, left to
// right, so later blocks sit on top of earlier ones (same-height neighbours merge into flat
// ground; a lower neighbour shows a cliff as thick as the height difference).
export const HEIGHT_TINT = { 1: "#EFEBE2", 2: "#DDD4BF", 3: "#C8BA98", 4: "#AF9F78" };

// Top / right / bottom face colours per height.
export const FACE = {
  1: { top: "#EFEBE2", right: "#C1B7A2", bottom: "#D3CBB8" },
  2: { top: "#DDD4BF", right: "#AB9E82", bottom: "#BFB399" },
  3: { top: "#C8BA98", right: "#94866A", bottom: "#AA9B7C" },
  4: { top: "#AF9F78", right: "#7B6D52", bottom: "#928263" },
};
// A flooded square is water on every face.
export const WATER = {
  overlay: "rgba(62,122,146,0.66)",
  right: "#3F7487",
  bottom: "#5A8FA2",
  lines: "rgba(234,243,246,0.85)",
  label: "rgba(234,243,246,0.7)",
};
export const LEVEL_LINE = "rgba(42,46,44,0.62)";
export const SIDE_EDGE = "rgba(42,46,44,0.25)";
export const TOP_EDGE = "rgba(42,46,44,0.22)";
export const SHADOW_RGB = "30,34,32";

export const S_REF = 112; // every size below is for a 112 px square and scales with it
export const S_MAX = 112;
export const LEVEL_PX = 5; // lift per height level
export const SEA_BAND = 44; // coast band height
export const TRAY_PAD = { top: 20, left: 20, right: 12, bottom: 12 };

export function heightOf(terrain, key) {
  const h = terrain?.[key];
  return Number.isFinite(Number(h)) && h !== null && h !== undefined ? Number(h) : null;
}

export const heightLabel = (h) => `▲${h}`;
export const heightLine = (h) => `Height ${heightLabel(h)}`;

const rcOf = (key) => key.split(",").map(Number);

// Board geometry for square size S (grid content coordinates: square r,c stands on
// [c*S, (c+1)*S] x [r*S, (r+1)*S]).
//   face(key): the full top face {left, top, width, height, d}
//   vis(key) : the part of the top face no later block covers (a higher right / lower / lower-right
//              neighbour hides a strip as thick as the height difference); every marking sits here.
//   shade(key): the shadows higher neighbours cast on this top face (light from the top-left).
export function boardGeom(terrain, rows, cols, S) {
  const k = S / S_REF;
  const h = (r, c) => (r < 0 || c < 0 || r >= rows || c >= cols ? 0 : heightOf(terrain, `${r},${c}`) ?? 1);
  const lift = (levels) => LEVEL_PX * k * levels;
  const face = (key) => {
    const [r, c] = rcOf(key);
    const d = lift(h(r, c));
    return { left: c * S - d, top: r * S - d, width: S, height: S, d };
  };
  const vis = (key) => {
    const [r, c] = rcOf(key);
    const h0 = h(r, c);
    const oR = lift(Math.max(0, h(r, c + 1) - h0));
    const oB = lift(Math.max(0, h(r + 1, c) - h0, h(r + 1, c + 1) - h0));
    const f = face(key);
    return { ...f, width: S - oR, height: S - oB, oR, oB };
  };
  const shade = (key) => {
    const [r, c] = rcOf(key);
    const h0 = h(r, c);
    const hl = h(r, c - 1);
    const ht = h(r - 1, c);
    const hd = h(r - 1, c - 1);
    return {
      left: Math.max(0, hl - h0),
      top: Math.max(0, ht - h0),
      corner: hd > h0 && hd > hl && hd > ht ? hd - h0 : 0,
    };
  };
  return { S, k, rows, cols, h, lift, face, vis, shade, width: cols * S, height: rows * S, z: (r, c) => 1 + r * cols + c };
}

// Layers drawn over every block (all squares use z 1..rows*cols).
export const LAYER = { conn: 102, walls: 104, weather: 104, markers: 105, picker: 106, thief: 107, preview: 150, hover: 120 };

// STREET (v3 step 7): every building square shows half a road + a sidewalk on all four sides, so
// neighbouring building squares join into one street. A Green Street turns the sidewalk into a
// verge with a rain swale and street trees. Colours match prompts/building_tile_green_street_mockup.svg.
export const STREET = {
  road: "#888780",
  centreLine: "#FAC775",
  sidewalk: "#D3D1C7",
  plot: "#F1EFE8",
  verge: "#97C459",
  swale: "#85B7EB",
  tree: "#3B6D11",
};
